import {
  ESPLoader,
  Transport,
  type FlashFreqValues,
  type FlashModeValues,
  type FlashOptions,
  type FlashSizeValues,
  type IEspLoaderTerminal,
} from "esptool-js";

const CODEWIZ_FIRMWARE_URL = "/scratch-gui/static/serve/firmware/codewiz_firmware.bin";
const CODEWIZ_FIRMWARE_ADDRESS = 0x10000;
const FLASH_BAUDRATE = 2_000_000;
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const SLIP_RUN_APP1 = new Uint8Array([0xc0, 0x00, 0xe2, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xc0]);

// ESP32 RTC_CNTL_OPTION1_REG (0x3FF48034) bit0 = FORCE_DOWNLOAD_BOOT
export const BOOTLOADER_CMD =
  "try:\n" +
  " from machine import mem32,reset\n" +
  " mem32[0x3FF48034]=mem32[0x3FF48034]|1\n" +
  " reset()\n" +
  "except:\n" +
  " import machine;machine.bootloader()\n";

export interface FlashFirmwareOptions {
  board?: string;
  onProgress?: (percent: number) => void;
  onLog?: (line: string) => void;
  /**
   * true = machine.reset()으로 ROM 부트로더 진입 완료.
   * CH340 USB 재열거 후 새 SerialPort 객체를 onconnect+폴링으로 획득.
   * 포트를 직접 열지 않고 esptool-js Transport에 넘겨 clean하게 open.
   */
  bootloaderReady?: boolean;
}

export async function flashCodewizFirmware(opts: FlashFirmwareOptions = {}): Promise<void> {
  if (opts.board && opts.board !== "CODEWIZ") {
    throw new Error(`${opts.board} 펌웨어 업로드는 아직 웹에서 지원되지 않습니다.`);
  }
  const serial = typeof navigator !== "undefined" ? navigator.serial : undefined;
  if (!serial) {
    throw new Error(
      "iPad/Safari에서는 펌웨어 업로드가 지원되지 않습니다.\nPC/Mac의 Chrome 또는 Edge 브라우저를 사용해 주세요.",
    );
  }

  opts.onProgress?.(1);
  const terminal = makeTerminal(opts.onLog);
  let port: SerialPort;

  if (opts.bootloaderReady) {
    // ── machine.reset() 후 경로 ─────────────────────────────────────────
    // Gemini: CH340은 reset 후 USB 재열거 → 기존 SerialPort 객체 무효.
    // Codex: onconnect 이벤트 + getPorts() 폴링 병행, VID 0x1a86 필터.
    //        포트를 여기서 직접 열지 말고 esptool Transport에 맡겨야
    //        reader/writer 상태 문제 없이 clean open 가능.
    opts.onLog?.("[flash] USB 재열거 대기 (onconnect + 폴링)...");
    port = await waitForNewPort(serial, 8000, opts.onLog);
    opts.onLog?.("[flash] 새 SerialPort 획득 → esptool-js에 전달");

  } else {
    // ── 연결 안 된 상태 fallback 경로 ──────────────────────────────────
    const ports = await serial.getPorts();
    if (!ports.length) {
      throw new Error("연결된 포트가 없습니다. 먼저 코드위즈를 연결해 주세요.");
    }
    port = ports[0];
    opts.onLog?.("[flash] 포트 열기 (119500, partition-0 fallback)...");
    await port.open({ baudRate: 119500 });

    try {
      const writer = port.writable!.getWriter();
      const enc = new TextEncoder();

      opts.onLog?.("[flash] runApp1 전송...");
      await writer.write(SLIP_RUN_APP1);
      await delay(1500);

      opts.onLog?.("[flash] Raw REPL → FORCE_DOWNLOAD_BOOT + reset()...");
      await writer.write(new Uint8Array([0x02, 0x03, 0x03, 0x01]));
      await delay(200);
      await writer.write(enc.encode(BOOTLOADER_CMD));
      await writer.write(new Uint8Array([0x04]));
      writer.releaseLock();

      const textDec = new TextDecoder();
      let replResp = "";
      const reader = port.readable!.getReader();
      const deadline = Date.now() + 1500;
      try {
        while (Date.now() < deadline) {
          const { value, done } = await Promise.race([
            reader.read(),
            delay(Math.max(50, deadline - Date.now())).then(
              () => ({ value: undefined as Uint8Array | undefined, done: true as const }),
            ),
          ]);
          if (done) break;
          if (value) {
            replResp += textDec.decode(value);
            if (replResp.includes("Traceback") || replResp.includes("Error")) break;
          }
        }
      } finally {
        reader.cancel().catch(() => undefined);
        reader.releaseLock();
      }
      opts.onLog?.(`[flash] REPL 응답: ${JSON.stringify(replResp.substring(0, 80))}`);

    } catch (e) {
      await port.close().catch(() => {});
      throw e;
    }

    await port.close().catch(() => {});
    opts.onProgress?.(3);

    // fallback에서도 재열거 후 새 포트 획득
    port = await waitForNewPort(serial, 6000, opts.onLog);
  }

  // ── esptool-js 동기화 ───────────────────────────────────────────────
  // 포트가 닫힌 상태로 Transport에 전달 → transport.connect()가 port.open() 담당.
  // no_reset: DTR/RTS 없이 sync만 → 이미 ROM 부트로더 진입 완료이므로 충분.
  opts.onProgress?.(3);
  const transport = new Transport(port, false);
  const loader = createLoader(transport, terminal);

  try {
    opts.onLog?.("[flash] esptool-js 동기화 (no_reset)...");
    await loader.main("no_reset");
    opts.onLog?.("[flash] 동기화 성공!");

    opts.onProgress?.(5);
    const firmware = await fetchFirmware();

    const flashOptions: FlashOptions = {
      fileArray: [{ data: firmware, address: CODEWIZ_FIRMWARE_ADDRESS }],
      flashMode: "dio" as FlashModeValues,
      flashFreq: "40m" as FlashFreqValues,
      flashSize: "4MB" as FlashSizeValues,
      eraseAll: false,
      compress: true,
      reportProgress: (_fileIndex, written, total) => {
        const ratio = total > 0 ? written / total : 0;
        opts.onProgress?.(5 + Math.round(ratio * 94));
      },
    };

    await loader.writeFlash(flashOptions);
    opts.onProgress?.(100);
    await loader.after("hard_reset");
  } catch (e) {
    const errMsg = (e as Error).message || String(e);
    throw new Error(
      `CodeWiz 부트로더 연결 실패. USB 케이블을 분리 후 재연결한 뒤 다시 시도해 주세요.\n(${errMsg})`,
    );
  } finally {
    await transport.disconnect().catch(() => undefined);
  }
}

/**
 * USB 재열거 후 새 SerialPort를 탐지하여 반환 (포트를 열지 않음).
 * Codex 권고: onconnect 이벤트 + getPorts() 폴링 병행, VID 0x1a86 우선 선택.
 */
function waitForNewPort(
  serial: Serial,
  timeoutMs: number,
  onLog?: (line: string) => void,
): Promise<SerialPort> {
  return new Promise((resolve, reject) => {
    let done = false;

    const finish = (port: SerialPort) => {
      if (done) return;
      done = true;
      clearInterval(pollId);
      clearTimeout(timerId);
      serial.removeEventListener("connect", connectHandler as EventListener);
      resolve(port);
    };

    const timerId = setTimeout(() => {
      if (done) return;
      done = true;
      clearInterval(pollId);
      serial.removeEventListener("connect", connectHandler as EventListener);
      // 타임아웃 최후 시도
      serial.getPorts().then((ports) => {
        const p = ports.find((p) => p.getInfo().usbVendorId === 0x1a86) ?? ports[0];
        if (p) resolve(p);
        else reject(new Error("포트 재연결 타임아웃. USB 케이블을 확인해 주세요."));
      }).catch(() => reject(new Error("포트 재연결 타임아웃")));
    }, timeoutMs);

    // onconnect 이벤트 (재열거 완료 시 Chrome이 즉시 발생)
    const connectHandler = (e: Event) => {
      const p = (e as unknown as { port?: SerialPort }).port;
      if (p && p.getInfo().usbVendorId === 0x1a86) {
        onLog?.("[flash] onconnect 이벤트로 포트 감지");
        finish(p);
      }
    };
    serial.addEventListener("connect", connectHandler as EventListener);

    // getPorts() 폴링 (이벤트 누락·이미 연결 상태 보완)
    const pollId = setInterval(async () => {
      if (done) return;
      try {
        const ports = await serial.getPorts();
        const p = ports.find((p) => p.getInfo().usbVendorId === 0x1a86);
        if (p) {
          onLog?.("[flash] getPorts() 폴링으로 포트 감지");
          finish(p);
        }
      } catch { /* ignore */ }
    }, 400);
  });
}

function createLoader(transport: Transport, terminal: IEspLoaderTerminal): ESPLoader {
  return new ESPLoader({
    transport,
    baudrate: FLASH_BAUDRATE,
    terminal,
    debugLogging: false,
  });
}

async function fetchFirmware(): Promise<Uint8Array> {
  const url = new URL(CODEWIZ_FIRMWARE_URL, window.location.origin).href;
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) {
    throw new Error(`CodeWiz 펌웨어 파일을 가져오지 못했습니다. HTTP ${res.status}`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

function makeTerminal(onLog?: (line: string) => void): IEspLoaderTerminal {
  return {
    clean() {},
    write(data: string) {
      if (data) onLog?.(data);
    },
    writeLine(data: string) {
      if (data) onLog?.(data);
    },
  };
}
